import React, { useState, useRef, useEffect } from "react";
import "./style.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
  setPaginationModel,
  setFilterModel,
  setRowSelectionModel,
  resetPage,
} from "../store/slices/DmsDataGridPro/dmsDataGridProPageSlice";
import { Condition } from "../services/Models/BaseModel";
import DataGridPro from "@vapor/v3-components/DataGridPro";
import {
  GridColDef,
  GridRowsProp,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridSlots,
  GridColumnVisibilityModel,
  GridApiPro,
  GridActionsColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridRowOrderChangeParams,
  GridPinnedColumnFields,
  GridSortModel,
  GridColumnGroupingModel,
  GridCallbackDetails,
  GridPreferencePanelParams,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  MuiEvent,
  GridDensity,
  GridCellParams,
  GridCellEditStopParams,
  GridToolbarQuickFilter,
  GridFooterContainer,
  GridPagination,
  GridFilterModel,
  GridLogicOperator,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import {
  enUS,
  itIT,
  frFR,
  esES,
  deDE,
  roRO,
} from "@mui/x-data-grid-pro/locales";
import {
  IDmsPageRequest,
  IDmsPageResponse,
} from "../services/Models/DmsPagination";
import { DataGridVaporToolbar } from "@vapor/v3-components/DataGrid/DataGridVaporToolbar";
import {
  Box,
  Button,
  EmptyState,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@vapor/v3-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButtonStyle } from "@vapor/v3-components/DataGrid";
import {
  faTableColumns,
  faLineHeight,
  faDownload,
  faFileCsv,
  faFileExcel,
  faPrint,
} from "@fortawesome/pro-regular-svg-icons";
import { SxProps, Theme } from "@mui/system";
import {
  getDefaultDmsPageRequest,
  ISearchFilter,
  IDmsOrderByItem,
} from "../services/Models/DmsPagination";
import { isBackwardNavigation, getLogonInfoFromCookies } from "../utils/util";
import { getColumnVisibility } from "./StandardColumns";
import DmsDataGridProFunctions from "./DmsDataGridProFunctions";
import { CustomQuickFilter } from "./CustomQuickFilter";
import * as XLSX from "xlsx";

const DataGridCustomToolbar: React.FC<{
  setToolbarColumnButton: (button: HTMLButtonElement | null) => void;
  selected: any[];
  contentleft?: any;
  showQuickFilter?: boolean;
  noExport?: boolean;
}> = ({
  setToolbarColumnButton,
  selected,
  contentleft,
  showQuickFilter,
  noExport,
}) => {
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const apiRef = useGridApiContext();

  // Function helper to get the formatted cell value
  const getFormattedCellValue = (id: any, col: any): any => {
    const row = apiRef.current.getRow(id);
    let value: any;

    // 1. get value using valueGetter if available
    if (col.valueGetter && typeof col.valueGetter === "function") {
      try {
        const cellValue = apiRef.current.getCellValue(id, col.field);
        value = (col.valueGetter as any)(cellValue, row, col, apiRef);
      } catch (error) {
        console.warn(`Error in valueGetter for ${col.field}:`, error);
        value = apiRef.current.getCellValue(id, col.field);
      }
    } else {
      value = apiRef.current.getCellValue(id, col.field);
    }

    // 2. Apply valueFormatter if available
    if (col.valueFormatter && typeof col.valueFormatter === "function") {
      try {
        value = col.valueFormatter(value, row, col, apiRef);
      } catch (error) {
        console.warn(`Error in valueFormatter for ${col.field}:`, error);
      }
    }

    return value;
  };

  const handleExportMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCsv = () => {
    try {
      const visibleRowIds = apiRef.current.getAllRowIds();
      const visibleColumns = apiRef.current.getVisibleColumns();

      const csvData: string[][] = [];

      // Headers
      const headers = visibleColumns
        .filter((col) => !col.disableExport)
        .map((col) => col.headerName || col.field);
      csvData.push(headers);

      // Data
      visibleRowIds.forEach((id) => {
        const csvRow: string[] = [];

        visibleColumns.forEach((col) => {
          if (!col.disableExport) {
            // Use helper function to get the already formatted value
            let value = getFormattedCellValue(id, col);

            // Convert to string for CSV
            let csvFormattedValue = value != null ? String(value) : "";

            // You can keep the cleanup of malformed characters if necessary
            if (typeof csvFormattedValue === "string") {
              csvFormattedValue = csvFormattedValue
                .replace(/â€™/g, "'")
                .replace(/â€œ/g, '"')
                .replace(/â€/g, '"')
                .replace(/â€"/g, "-");
            }

            // Escape for CSV
            const escapedValue =
              csvFormattedValue.includes(";") ||
              csvFormattedValue.includes('"') ||
              csvFormattedValue.includes("\n")
                ? `"${csvFormattedValue.replace(/"/g, '""')}"`
                : csvFormattedValue;

            csvRow.push(escapedValue);
          }
        });

        csvData.push(csvRow);
      });

      // Create CSV with UTF-8 BOM
      const csvContent = csvData.map((row) => row.join(";")).join("\n");
      const BOM = "\uFEFF";
      const csvWithBOM = BOM + csvContent;

      // Download
      const blob = new Blob([csvWithBOM], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `export_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error during CSV export:", error);
    }

    handleExportMenuClose();
  };

  const handleExportExcel = () => {
    try {
      const visibleRowIds = apiRef.current.getAllRowIds();
      const visibleColumns = apiRef.current.getVisibleColumns();

      const excelData = visibleRowIds.map((id) => {
        const exportRow: any = {};

        visibleColumns.forEach((col) => {
          if (!col.disableExport) {
            // Use helper function to get the already formatted value
            const value = getFormattedCellValue(id, col);

            const headerName = col.headerName || col.field;
            exportRow[headerName] = value != null ? String(value) : "";
          }
        });

        return exportRow;
      });

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data");

      const fileName = `export_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error during Excel export:", error);
    }

    handleExportMenuClose();
  };

  const handlePrint = () => {
    try {
      const visibleRowIds = apiRef.current.getAllRowIds();
      const visibleColumns = apiRef.current
        .getVisibleColumns()
        .filter((col) => !col.disableExport);

      // Create an HTML table for printing
      let printContent = `
        <html>
        <head>
          <title>Stampa Tabella</title>
          <style>
            @media print {
              body { font-family: Arial, sans-serif; font-size: 12px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: left; 
                vertical-align: top;
              }
              th { 
                background-color: #f5f5f5; 
                font-weight: bold; 
                font-size: 11px;
              }
              td { font-size: 10px; }
              .page-header { 
                text-align: center; 
                margin-bottom: 20px; 
                font-size: 16px; 
                font-weight: bold; 
              }
              @page { margin: 1.5cm; }
            }
          </style>
        </head>
        <body>
          <div class="page-header">Esportazione Dati - ${new Date().toLocaleDateString(
            "it-IT"
          )}</div>
          <table>
            <thead>
              <tr>
      `;

      // Headers
      visibleColumns.forEach((col) => {
        printContent += `<th>${col.headerName || col.field}</th>`;
      });
      printContent += `
              </tr>
            </thead>
            <tbody>
      `;

      // Data
      visibleRowIds.forEach((id) => {
        printContent += "<tr>";

        visibleColumns.forEach((col) => {
          // Use helper function to get the already formatted value
          const value = getFormattedCellValue(id, col);

          // Convert to string
          let printValue = value != null ? String(value) : "";

          // Escape HTML
          printValue = printValue
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");

          printContent += `<td>${printValue}</td>`;
        });

        printContent += "</tr>";
      });

      printContent += `
            </tbody>
          </table>
        </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();

        // Start printing when the content is loaded
        printWindow.onload = () => {
          printWindow.print();
          // Close the window after printing (optional)
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        };
      }
    } catch (error) {
      console.error("Error during printing:", error);
    }

    handleExportMenuClose();
  };

  return (
    <DataGridVaporToolbar
      selected={selected?.length > 0}
      contentLeft={contentleft}
      contentRight={
        <div>
          <Box sx={{ display: "flex", gap: "8px" }}>
            {/* {showQuickFilter && <GridToolbarQuickFilter />} */}
            {showQuickFilter && <CustomQuickFilter />}
            <GridToolbarColumnsButton
              ref={(button) => setToolbarColumnButton(button)}
              slotProps={{
                button: {
                  sx: {
                    ...IconButtonStyle,
                  },
                  startIcon: <FontAwesomeIcon icon={faTableColumns} />,
                },
              }}
            />
            <GridToolbarDensitySelector
              slotProps={{
                button: {
                  sx: {
                    ...IconButtonStyle,
                  },
                  startIcon: <FontAwesomeIcon icon={faLineHeight} />,
                },
                tooltip: {
                  title: "Density",
                },
              }}
            />

            {!noExport && (
              <>
                <Button
                  sx={{
                    ...IconButtonStyle,
                  }}
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={handleExportMenuClick}
                  size="small"
                >
                  Export
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={handleExportMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <MenuItem onClick={handleExportCsv}>
                    <ListItemIcon>
                      <FontAwesomeIcon icon={faFileCsv} size="sm" />
                    </ListItemIcon>
                    <ListItemText>Export CSV</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleExportExcel}>
                    <ListItemIcon>
                      <FontAwesomeIcon icon={faFileExcel} size="sm" />
                    </ListItemIcon>
                    <ListItemText>Export Excel</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handlePrint}>
                    <ListItemIcon>
                      <FontAwesomeIcon icon={faPrint} size="sm" />
                    </ListItemIcon>
                    <ListItemText>Stampa</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </div>
      }
    />
  );
};

interface IDmsDataGridProColumn {
  field: string;
  headerName?: any;
  editable: boolean;
  width?: number;
  type: string;
  filterOperators?: any;
  filterable?: boolean;
  disableReorder?: boolean; //if not specified is true
  pinnable?: boolean; //if not specified is true
  sortable?: boolean; //if not specified is true
  resizable?: boolean;
  renderCell?: any;
}

export interface IExtendedGridActionsColDef
  extends GridActionsColDef<any, any, any> {
  n?: number;
  k?: string;
}

interface IDmsDataGridPro {
  apiRef?: React.MutableRefObject<GridApiPro>;
  // rows: any[];
  // columns: GridColDef[];
  data: {
    columns: IExtendedGridActionsColDef[];
    columnVisibilityModel: GridColumnVisibilityModel;
    rows: [];
    pageRequest?: IDmsPageRequest;
    pageResponse?: IDmsPageResponse<any>;
  };
  // columnVisibilityModel?: GridColumnVisibilityModel
  dmsDataFunction(): void;
  contentleft?: any;
  emptyStateSize?: "small" | "medium" | "large";
  disableIconEmptyState?: boolean;
  sxProp?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  contextId: string;
  pagination?: boolean; //if is set on false, the table will be with only 1page as a list. else, if you want to customize page size use the props below
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  paginationModel?: GridPaginationModel;
  keepNonExistentRowsSelected?: boolean;
  checkboxSelection?: boolean;
  radioSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  onRowSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel
  ) => void;
  onFilterModelChange?: (conditions: Condition[], quickSearch: string) => void;
  rowSelectionModel?: GridRowSelectionModel;
  hideFooter?: boolean;
  onRowClick?: any;
  getRowId?: any;
  rowReordering?: boolean;
  onRowOrderChange?: (rowOrderChange: GridRowOrderChangeParams) => void;
  columnGroupingModel?: GridColumnGroupingModel;
  isCellEditable?: (param: GridCellParams) => boolean;
  onCellEditStop?: (param: GridCellEditStopParams) => void;
  processRowUpdate?: (newRow: any, oldRow: any) => void;
  currentLanguage?: "it" | "en" | "es" | "fr" | "de" | "ro";
  onSortModelChange?: (
    newModel: GridSortModel,
    details: GridCallbackDetails
  ) => void;
  noExport?: boolean;
  filterMode?: boolean;
}

export type DmsDataGridProps = React.ComponentProps<typeof DataGridPro> & {
  contextId: string;
  rows: any[];
  columns: GridColDef[];
  data: {
    columns: IExtendedGridActionsColDef[];
    columnVisibilityModel: GridColumnVisibilityModel;
    rows: [];
    pageRequest?: IDmsPageRequest;
    pageResponse?: IDmsPageResponse<any>;
  };
  contentleft?: any;
  emptyStateSize?: "small" | "medium" | "large";
  disableIconEmptyState?: boolean;
};

type Widths = {
  [key: string]: number;
};
type Data = {
  columns: IExtendedGridActionsColDef[];
  columnVisibilityModel: GridColumnVisibilityModel;
  rows: any[];
  pageRequest: IDmsPageRequest;
  pageResponse: any;
};

type SupportedLanguages = "en" | "it" | "fr" | "es" | "de" | "ro";

export interface LocalStorageGridData {
  widths: Widths;
  columnVisibilityModel: GridColumnVisibilityModel;
  columnOrder: string[];
  sortModel: GridSortModel;
  pageSize: number;
  pinnedColumns: GridPinnedColumnFields;
  density: GridDensity;
}

const DmsDataGridPro: React.FC<IDmsDataGridPro> = (props) => {
  const lastFilterConditions = useRef<Condition[]>([]);
  const lastQuickSearch = useRef("");
  const localStorageGrid = useRef(props.contextId);
  const dispatch = useDispatch<AppDispatch>();
  const pageState = useSelector(
    (state: RootState) =>
      state.dmsDataGridProPage[props.contextId] ||
      state.dmsDataGridProPage.default
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [oldPageRequest, setOldPageRequest] = useState<IDmsPageRequest>(
    getDefaultDmsPageRequest()
  );
  const [data, setData] = useState<Data>({
    columns: [],
    columnVisibilityModel: {},
    rows: [],
    pageRequest: getDefaultDmsPageRequest(),
    pageResponse: null,
  });
  const [rowCount, setRowCount] = useState<number>(0);

  const [selected, setSelected] = useState<any[]>([]);
  const [toolbarColumnButton, setToolbarColumnButton] =
    React.useState<HTMLButtonElement | null>(null);

  const [sortModel, setSortModel] = React.useState<GridSortModel>(() => {
    let localStorageGridData: LocalStorageGridData = JSON.parse(
      localStorage.getItem(localStorageGrid.current) ?? "null"
    );
    if (localStorageGridData) {
      let model = localStorageGridData.sortModel ?? [];
      return model;
    } else return [];
  });

  const [pinnedColumns, setPinnedColumns] = React.useState(() => {
    // Carica le colonne bloccate dal localstorage
    let localStorageGridData: LocalStorageGridData = JSON.parse(
      localStorage.getItem(localStorageGrid.current) ?? "null"
    );
    if (localStorageGridData) {
      let pinnedColumns = localStorageGridData.pinnedColumns ?? {
        left: [],
        right: [],
      };
      return pinnedColumns;
    } else return { left: [], right: [] };
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    let localStorageGridData: LocalStorageGridData = JSON.parse(
      localStorage.getItem(localStorageGrid.current) ?? "null"
    );
    if (localStorageGridData) {
      let columnVisibilityModel =
        localStorageGridData.columnVisibilityModel ?? {};
      return columnVisibilityModel;
    } else return {};
  });

  const [density, setDensity] = useState<GridDensity>(() => {
    let localStorageGridData: LocalStorageGridData = JSON.parse(
      localStorage.getItem(localStorageGrid.current) ?? "null"
    );
    if (localStorageGridData) {
      let density = localStorageGridData.density ?? "standard";
      return density;
    } else return "standard";
  });

  const columnOrder = useRef<string[]>([]);
  const widths = useRef<Widths>({});

  const actionCols = useRef<string[]>([]);

  const { t } = useTranslation();
  const locales: Record<SupportedLanguages, any> = {
    en: enUS.components.MuiDataGrid.defaultProps.localeText,
    it: itIT.components.MuiDataGrid.defaultProps.localeText,
    fr: frFR.components.MuiDataGrid.defaultProps.localeText,
    es: esES.components.MuiDataGrid.defaultProps.localeText,
    de: deDE.components.MuiDataGrid.defaultProps.localeText,
    ro: roRO.components.MuiDataGrid.defaultProps.localeText,
  };

  const currentLanguage = (): string => {
    if (props.currentLanguage) return props.currentLanguage;
    const loginInfo = getLogonInfoFromCookies();
    return loginInfo && loginInfo.Language ? loginInfo.Language : "it";
  };

  const getSortings = (sModel: GridSortModel) => {
    //check the sortModel, for the moment, manage only one sort
    let fieldsToSort: IDmsOrderByItem[] = [];
    if (sModel.length === 0) {
      fieldsToSort = [];
    } else {
      for (let i = 0; i < sModel.length; i++) {
        let sortDirection = 0;
        if (sModel[i].sort === "asc") {
          sortDirection = 1;
        } else if (sModel[i].sort === "desc") {
          sortDirection = -1;
        }
        let fieldToSort: IDmsOrderByItem = {
          fieldDescriptor: [],
          direction: 0,
        };
        fieldToSort.fieldDescriptor = [sModel[i].field];
        fieldToSort.direction = sortDirection;
        fieldsToSort = [...fieldsToSort, fieldToSort];
      }
    }
    return fieldsToSort;
  };

  const orderedColumns = (columnsToOrder: IExtendedGridActionsColDef[]) => {
    const orderField: { [key: string]: number } = {};
    columnOrder.current.forEach((field, index) => {
      orderField[field] = index;
    });
    //aggiungo il valueGetter necessario in caso di export della tabella
    const columnsWithValueGetter = columnsToOrder.map((column) => {
      if (column.valueGetter) {
        return column;
      }

      return {
        ...column,
        valueGetter: (value: any, row: any, column: any, apiRef: any) => {
          // Gestisci diversi tipi di dati
          if (value === null || value === undefined) {
            return "";
          }

          // Per date, restituisci il valore formattato
          if (value instanceof Date) {
            return value.toLocaleDateString("it-IT");
          }

          // Per boolean, converti in testo
          if (typeof value === "boolean") {
            return value ? "Sì" : "No";
          }

          // Per tutti gli altri tipi, restituisci il valore così com'è
          return String(value);
        },
      };
    });

    const newColumnsOrdered = columnsWithValueGetter.sort(
      (a: IExtendedGridActionsColDef, b: IExtendedGridActionsColDef) => {
        const aOrder = orderField[a.field];
        const bOrder = orderField[b.field];

        if (aOrder !== undefined && bOrder !== undefined) {
          // Se entrambi gli elementi sono nell'ordine, confronta i loro indici
          return aOrder - bOrder;
        } else if (aOrder !== undefined) {
          // Se solo a è nell'ordine, viene prima
          return -1;
        } else if (bOrder !== undefined) {
          // Se solo b è nell'ordine, viene prima
          return 1;
        } else {
          // Se nessuno è nell'ordine, mantieni l'ordine originale (opzionale)
          return 0;
        }
      }
    );
    return newColumnsOrdered;
  };

  const setRefWidths = (newWidths: Widths) => {
    widths.current = newWidths;
    const lsGridDataToSave: LocalStorageGridData = {
      widths: widths.current,
      columnVisibilityModel: columnVisibilityModel,
      columnOrder: columnOrder.current,
      sortModel: sortModel,
      pageSize: pageState.paginationModel.pageSize,
      pinnedColumns: pinnedColumns,
      density: density,
    };
    localStorage.setItem(
      localStorageGrid.current,
      JSON.stringify(lsGridDataToSave)
    );
  };

  const setRefColumnOrder = (newColumnOrder: string[]) => {
    columnOrder.current = newColumnOrder;
    const lsGridDataToSave: LocalStorageGridData = {
      widths: widths.current,
      columnVisibilityModel: columnVisibilityModel,
      columnOrder: columnOrder.current,
      sortModel: sortModel,
      pageSize: pageState.paginationModel.pageSize,
      pinnedColumns: pinnedColumns,
      density: density,
    };
    localStorage.setItem(
      localStorageGrid.current,
      JSON.stringify(lsGridDataToSave)
    );
  };

  useEffect(() => {
    // Carica la larghezza delle colonne dal localstorage
    let localStorageGridData: LocalStorageGridData = JSON.parse(
      localStorage.getItem(localStorageGrid.current) ?? "null"
    );
    if (localStorageGridData) {
      setRefWidths(localStorageGridData.widths ?? {});
    } else {
      setRefWidths({});
    }
    setRefColumnOrder(
      localStorageGridData
        ? localStorageGridData.columnOrder
          ? localStorageGridData.columnOrder
          : data.columns.map((col) => col.field)
        : data.columns.map((col) => col.field)
    );

    //if return from editpage, is necessary set pagemodel and filter from redux
    //otherwise set with localstorage (only for page number) or with default
    let newPageRequest: IDmsPageRequest = data.pageRequest;
    let pgModel: GridPaginationModel = { page: 0, pageSize: 5 };
    //if present in redux I use that
    //otherwise I check in localstorage pagesize
    //otherwise in props otherwise default page 0 and pagesize 5
    if (!isBackwardNavigation()) {
      dispatch(resetPage({ contextId: props.contextId }));
      if (localStorageGridData) {
        pgModel.pageSize =
          localStorageGridData.pageSize ?? props.defaultPageSize ?? 5;
        newPageRequest.pageSize =
          localStorageGridData.pageSize ?? props.defaultPageSize ?? 5;
      } else {
        pgModel.pageSize =
          props.defaultPageSize ??
          (props.paginationModel ? props.paginationModel.pageSize : 5);
        newPageRequest.pageSize =
          props.defaultPageSize ??
          (props.paginationModel ? props.paginationModel.pageSize : 5);
      }
      pgModel.page = props.paginationModel ? props.paginationModel.page : 0;
      if (props.pagination ?? true)
        newPageRequest.page = props.paginationModel
          ? props.paginationModel!.page++
          : 1;
      else newPageRequest.page = 0;
      dispatch(
        setPaginationModel({
          contextId: props.contextId,
          paginationModel: pgModel,
        })
      );
      dispatch(
        setRowSelectionModel({
          contextId: props.contextId,
          rowSelectionModel: props.rowSelectionModel ?? [],
        })
      );
    } else {
      newPageRequest.page = pageState.paginationModel.page;
      if (props.pagination ?? true) newPageRequest.page++;
      newPageRequest.pageSize = pageState.paginationModel.pageSize;

      let searchFilter: ISearchFilter = { logic: "and", searchConditions: [] };
      // searchFilter.logic = filterModel.logicOperator === GridLogicOperator.Or ? "or" : "and"
      // filterModel.items.forEach((el, index) => {
      //   searchFilter.searchConditions!.push({
      //     fieldDescriptor: [el.field],
      //     condition: el.operator,
      //     values: el.value,
      //   })
      // })
      newPageRequest.searchFilters = [searchFilter];
      // if (props.onRowSelectionModelChange && props.rowSelectionModel) {
      //   props.onRowSelectionModelChange(rowSelectionModel)
      // }
    }
    // Load initial sort saved in local storage
    if (sortModel) {
      newPageRequest.orderBy = getSortings(sortModel);
    }
    if (props.data.pageRequest !== newPageRequest) {
      props.data.pageRequest = newPageRequest;
    }
  }, []);

  useEffect(() => {
    localStorageGrid.current = props.contextId;
    // Carica le colonne bloccate dal localstorage
    let localStorageGridData: LocalStorageGridData = JSON.parse(
      localStorage.getItem(localStorageGrid.current) ?? "null"
    );
    if (localStorageGridData) {
      let lsWidths = localStorageGridData.widths ?? {};
      // widths.current = lsWidths
      setRefWidths(lsWidths);
      let lsColumnVisibilityModel =
        localStorageGridData.columnVisibilityModel ?? {};
      setColumnVisibilityModel(lsColumnVisibilityModel);
      let lsColumnOrder = localStorageGridData.columnOrder ?? {};
      setRefColumnOrder(lsColumnOrder);
      let lsSortModel = localStorageGridData.sortModel ?? {};
      setSortModel(lsSortModel);
      let lsPaginationModel: GridPaginationModel = {
        page: pageState.paginationModel.page,
        pageSize:
          localStorageGridData.pageSize ??
          pageState.paginationModel.pageSize ??
          5,
      };
      dispatch(
        setPaginationModel({
          contextId: props.contextId,
          paginationModel: lsPaginationModel,
        })
      );
      let newPageRequest: IDmsPageRequest = data.pageRequest;
      newPageRequest.page = getDefaultDmsPageRequest().page;
      newPageRequest.pageSize = getDefaultDmsPageRequest().pageSize;
      // dispatch(setFilterModel({ contextId: props.contextId, filterModel: !isBackwardNavigation() ? { items: [] } : filterModel }))
      // dispatch(setFilterModel({ contextId: props.contextId, filterModel: filterModel }))
      // dispatch(setFilterModel({ contextId: props.contextId, filterModel: { items: [] } }))
      // if (typeof props.onFilterModelChange !== "undefined") {
      //   props.onFilterModelChange(filterModel)
      // }
      let searchFilter: ISearchFilter = {
        id: "grid_column_filter",
        logic: "and",
        searchConditions: [],
      };
      newPageRequest.searchFilters = [searchFilter];
      props.data.pageRequest = newPageRequest;
      // if (typeof props.onPaginationModelChange !== "undefined") props.onPaginationModelChange(lsPaginationModel)
      let lsPinnedColumns = localStorageGridData.pinnedColumns ?? {};
      setPinnedColumns(lsPinnedColumns);
    }
  }, [props.contextId]);

  useEffect(() => {
    if (props.data.columns.length === 0) {
      setData({ ...data, rows: props.data.rows });
      return;
    }
    let page = 0;
    let pageSize = pageState.paginationModel.pageSize;
    let totalCount = 0;
    if (props.data.pageResponse != null) {
      page = props.data.pageResponse.page;
      if (page > 0) page--;
      else page = 0;
      pageSize = props.data.pageResponse.pageSize;
      totalCount = props.data.pageResponse.totalCount ?? 0;
    }
    props.data.columns.forEach((col) => {
      if (typeof widths.current[col.field] !== "undefined") {
        col.width = widths.current[col.field];
      }
    });
    let appColVisibility = columnVisibilityModel;
    props.data.columns.forEach((col) => {
      if (col.type !== "actions") {
        col.filterable =
          props.rowReordering ?? false ? false : col.filterable ?? true;
        col.hideable = props.data.columnVisibilityModel[col.field];
        col.sortable = col.sortable ?? true;
        col.pinnable = true;
        col.resizable = true;
      } else {
        if (!actionCols.current.includes(col.field))
          actionCols.current.push(col.field);
        col.filterable = false;
        col.hideable = false;
        col.sortable = false;
        col.pinnable = false;
        col.resizable = false;
        if (pinnedColumns.right) {
          let isAlready = true;
          const appPinnedColumns = { ...pinnedColumns };
          if (!appPinnedColumns.right!.includes(col.field)) {
            isAlready = false;
            appPinnedColumns.right!.push(col.field);
          }
          if (!isAlready) {
            setPinnedColumns(appPinnedColumns);
          }
        } else setPinnedColumns({ ...pinnedColumns, right: [col.field] });
      }
      if (!props.data.columnVisibilityModel[col.field])
        appColVisibility[col.field] = false;
      else if (typeof appColVisibility[col.field] === "undefined")
        appColVisibility[col.field] = getColumnVisibility(col.field) ?? true;
      if (!(JSON.parse(JSON.stringify(col)).visible ?? true))
        appColVisibility[col.field] = false;
    });
    // if (Object.keys(columnOrder.current).length === 0)
    //   setRefColumnOrder(
    //     props.data.columns.map((col) => {
    //       return col.field
    //     })
    //   )
    //    setData({ ...data, columnVisibilityModel: props.data.columnVisibilityModel, columns: orderedColumns(applyFilterOperators(props.data.columns)), rows: props.data.rows })
    if (Object.keys(columnOrder.current).length === 0)
      setRefColumnOrder(
        props.data.columns.map((col) => {
          return col.field;
        })
      );
    setColumnVisibilityModel(appColVisibility);
    setData({
      ...data,
      //      columnVisibilityModel: props.data.columnVisibilityModel,
      columnVisibilityModel: appColVisibility,
      columns: orderedColumns(props.data.columns),
      rows: props.data.rows,
    });
    dispatch(
      setPaginationModel({
        contextId: props.contextId,
        paginationModel: { page: page, pageSize: pageSize },
      })
    );
    setRowCount(totalCount);
    let lsWidth: Widths = {};
    props.data.columns.forEach((col) => {
      lsWidth[col.field] = col.width!;
    });
    setRefWidths(lsWidth);
  }, [props.data.columns, props.data.columnVisibilityModel, props.data.rows]);

  useEffect(() => {
    const lsGridDataToSave: LocalStorageGridData = {
      widths: widths.current,
      columnVisibilityModel: columnVisibilityModel,
      columnOrder: columnOrder.current,
      sortModel: sortModel,
      pageSize: pageState.paginationModel
        ? pageState.paginationModel.pageSize
        : 5,
      pinnedColumns: pinnedColumns,
      density: density,
    };
    localStorage.setItem(
      localStorageGrid.current,
      JSON.stringify(lsGridDataToSave)
    );
  }, [
    widths,
    columnVisibilityModel,
    columnOrder,
    sortModel,
    pageState.paginationModel,
    pinnedColumns,
    density,
  ]);

  // useEffect(() => {
  //   // This effect runs when the component mounts and dismounts
  //   return () => {
  //     dispatch(restorePageState({ contextId: props.contextId }))
  //   }
  // }, [dispatch])

  //#endregion

  //#region HandleFunctions

  const onColumnVisibilityModelChange = (
    newColumnVisibilityModel: GridColumnVisibilityModel,
    details: GridCallbackDetails<any>
  ) => {
    // alert("handleColumnVisibilityModelChange")
    // setData({ ...data, columnVisibilityModel: newColumnVisibilityModel })
    setColumnVisibilityModel(newColumnVisibilityModel);
  };

  const onDensityChange = (newDensity: GridDensity) => {
    setDensity(newDensity); // Aggiorna lo stato
  };

  const onColumnWidthChange = (resizeCols: GridColumnResizeParams) => {
    const updatedWidths: Widths = {
      ...widths.current,
      [resizeCols.colDef.field]: resizeCols.width,
    };
    setRefWidths(updatedWidths);
  };

  const onColumnOrderChange = (e: GridColumnOrderChangeParams) => {
    try {
      const newOrder = [...columnOrder.current];
      const alignPosition =
        props.checkboxSelection || props.radioSelection ? 1 : 0;
      const [movedItem] = newOrder.splice(e.oldIndex - alignPosition, 1);
      newOrder.splice(e.targetIndex - alignPosition, 0, movedItem);
      setRefColumnOrder(newOrder);
    } catch {}
  };

  //switch pinned columns
  const onPinnedColumnsChange = (pinnedCol: GridPinnedColumnFields) => {
    if (actionCols.current.length > 0) {
      if (pinnedCol.right) {
        if (pinnedCol.right!.length > actionCols.current.length) {
          actionCols.current.forEach((col) => {
            pinnedCol.right! = pinnedCol.right!.filter(
              (item: string) => item !== col
            );
          });
          actionCols.current.forEach((col) => {
            pinnedCol.right!.push(col);
          });
        }
      }
    }
    // setAreDataRecived(false)
    setPinnedColumns(pinnedCol);
  };

  // const onRowSelectionModelChange = (rowSelection: GridRowSelectionModel) => {
  //   if (data.columns.length === 0) return;
  //   dispatch(
  //     setRowSelectionModel({
  //       contextId: props.contextId,
  //       rowSelectionModel: rowSelection,
  //     })
  //   );
  //   if (typeof props.onRowSelectionModelChange !== "undefined") {
  //     props.onRowSelectionModelChange(rowSelection);
  //   }
  // };
  const onSortModelChange = (
    newModel: GridSortModel,
    details: GridCallbackDetails
  ) => {
    // Without available columns there is nothing todo
    if (data.columns.length === 0) return;
    setSortModel(newModel);
    if (props.onSortModelChange) props.onSortModelChange(newModel, details);
    else if (props.pagination ?? true) {
      let newPageRequest: IDmsPageRequest = data.pageRequest;
      newPageRequest.orderBy = getSortings(newModel);
      props.data.pageRequest = newPageRequest;
      // setPaginationModel({ page: 0, pageSize: paginationModel.pageSize })
      dispatch(
        setPaginationModel({
          contextId: props.contextId,
          paginationModel: {
            page: 0,
            pageSize: pageState.paginationModel.pageSize,
          },
        })
      );
      if (props.pagination ?? true) {
        props.dmsDataFunction();
      }
    }
  };
  const onFilterModelChange = (filterModel: GridFilterModel) => {
    if (data.columns.length === 0) return;
    setFilterModel(filterModel);
    let quickSearch = "";
    if (
      filterModel.quickFilterValues &&
      filterModel.quickFilterValues?.length > 0
    ) {
      for (let i = 0; i < filterModel.quickFilterValues?.length; i++) {
        quickSearch +=
          (quickSearch !== "" ? " " : "") + filterModel.quickFilterValues[i];
      }
    }
    let conditions: Condition[] = [];
    let searchOperator =
      filterModel.logicOperator === GridLogicOperator.Or ? "or" : "and";
    filterModel.items.forEach((el, index) => {
      if (
        el.operator === "isEmpty" ||
        el.operator === "isNotEmpty" ||
        el.value !== undefined
      ) {
        conditions.push({
          LogicOperator: searchOperator,
          Field: el.field,
          SearchOperator: el.operator,
          Value: el.value,
        });
      }
    });
    if (
      JSON.stringify(conditions) !==
        JSON.stringify(lastFilterConditions.current) ||
      lastQuickSearch.current !== quickSearch
    ) {
      lastFilterConditions.current = conditions;
      lastQuickSearch.current = quickSearch;
      if (props.onFilterModelChange)
        props.onFilterModelChange(conditions, quickSearch);
    }
    // props.data.pageRequest!.conditions = conditions;
  };

  const onPaginationModelChange = (
    newPaginationModel: GridPaginationModel,
    details: GridCallbackDetails<any>
  ) => {
    let newPageRequest: IDmsPageRequest = data.pageRequest;
    newPageRequest.page = newPaginationModel.page + 1;
    newPageRequest.pageSize = newPaginationModel.pageSize;
    //setPaginationModel(newPaginationModel)
    dispatch(
      setPaginationModel({
        contextId: props.contextId,
        paginationModel: newPaginationModel,
      })
    );
    props.data.pageRequest = newPageRequest;
    if (props.pagination ?? true) {
      props.dmsDataFunction();
    }
    // if (typeof props.onPaginationModelChange !== "undefined") props.onPaginationModelChange(newPaginationModel)
  };

  // const onFilterModelChange = (filterModel: GridFilterModel) => {
  //   if (data.columns.length === 0) return;
  //   dispatch(
  //     setFilterModel({ contextId: props.contextId, filterModel: filterModel })
  //   );
  //   let searchFilter: ISearchFilter = { logic: "and", searchConditions: [] };
  //   searchFilter.logic =
  //     filterModel.logicOperator === GridLogicOperator.Or ? "or" : "and";
  //   filterModel.items.forEach((el, index) => {
  //     searchFilter.searchConditions!.push({
  //       fieldDescriptor: [el.field],
  //       condition: el.operator,
  //       values: el.value,
  //     });
  //   });
  //   props.data.pageRequest!.searchFilters = [searchFilter];
  // };

  const onPreferencePanelOpen = (
    params: GridPreferencePanelParams,
    event: MuiEvent<{}>,
    details: GridCallbackDetails
  ) => {
    // The preference panel is opened by filter settings or manage columns
    // The current page request is saved to check if something is changed when the panel close
    setOldPageRequest(structuredClone(data.pageRequest));
  };

  const onPreferencePanelClose = (
    params: GridPreferencePanelParams,
    event: MuiEvent<{}>,
    details: GridCallbackDetails
  ) => {
    // The preference panel is opened by filter settings or manage columns
    // If the panel is related to filter settings then if something is changed from the previous request then the data must be reloaded
    if (
      JSON.stringify(oldPageRequest.searchFilter) !==
      JSON.stringify(data.pageRequest.searchFilters)
    ) {
      props.data.pageRequest!.searchFilters = data.pageRequest.searchFilters;
      // props.data.pageRequest!.searchText = props.searchedText ?? ""
      if (props.pagination ?? true) {
        props.dmsDataFunction();
      }
    }
  };
  const CustomFooter: React.FC = () => {
    return (
      <GridFooterContainer>
        <GridPagination />
        {/* Nasconde solo il contatore dei record selezionati */}
      </GridFooterContainer>
    );
  };
  return (
    <DataGridPro
      className="custom-align"
      // {...props}
      {...data}
      // rows={props.rows}
      // columns={props.columns}
      pagination={props.pagination ?? true}
      paginationMode={props.pagination ?? true ? "server" : "client"}
      paginationModel={pageState.paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      onColumnWidthChange={onColumnWidthChange}
      onColumnOrderChange={onColumnOrderChange}
      keepNonExistentRowsSelected={props.keepNonExistentRowsSelected ?? true}
      checkboxSelection={props.checkboxSelection}
      disableRowSelectionOnClick={props.disableRowSelectionOnClick ?? false}
      onRowSelectionModelChange={(list: any) => {
        setSelected(list); // Get list of the selected items
      }}
      filterMode={
        props.filterMode ?? props.pagination ?? true ? "server" : "client"
      }
      filterModel={filterModel!}
      filterDebounceMs={1000}
      onFilterModelChange={onFilterModelChange}
      sortingMode={
        props.filterMode ?? props.pagination ?? true ? "server" : "client"
      }
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      onPreferencePanelOpen={onPreferencePanelOpen}
      onPreferencePanelClose={onPreferencePanelClose}
      pinnedColumns={pinnedColumns}
      onPinnedColumnsChange={onPinnedColumnsChange}
      pageSizeOptions={props.pageSizeOptions ?? [5, 10, 20, 50, 100]}
      rowCount={rowCount}
      getRowId={props.getRowId}
      onRowClick={(params, event) => {
        if (props.onRowClick != null) props.onRowClick(params, event);
      }}
      density={density}
      onDensityChange={onDensityChange}
      columnGroupingModel={props.columnGroupingModel}
      disableColumnFilter={false}
      sx={{
        margin: 1,
        overflow: "auto", // Abilita lo scrolling automatico
        flexGrow: 1, // Occupa lo spazio disponibile nel contenitore
      }}
      slots={{
        toolbar: DataGridCustomToolbar as GridSlots["toolbar"],
        noRowsOverlay: () => (
          <EmptyState
            buttonProps={[]}
            size={props.emptyStateSize ?? "medium"}
            disableIcon={props.disableIconEmptyState ?? false}
            title={t("noDataGrid")}
          />
        ),
        noResultsOverlay: () => (
          <EmptyState
            buttonProps={[]}
            size={props.emptyStateSize ?? "medium"}
            disableIcon={props.disableIconEmptyState ?? false}
            title={t("noFilteredDataGrid")}
          />
        ),
        // footer: CustomFooter, // Assegni il footer personalizzato nello slot
      }}
      slotProps={{
        panel: {
          anchorEl: toolbarColumnButton,
          placement: "bottom-start",
        } as any,
        toolbar: {
          setToolbarColumnButton: setToolbarColumnButton,
          selected: selected,
          contentleft: props.contentleft,
          showQuickFilter: true,
          noExport: props.noExport,
        } as any,
        filterPanel: { sx: { zIndex: 1300 } }, // 🔥 Forza un livello più alto per evitare problemi di overflow
      }}
      initialState={{ pinnedColumns: { left: [], right: ["_actions_"] } }}
      hideFooter={props.hideFooter ?? false}
      rowReordering={props.rowReordering ?? false}
      onRowOrderChange={props.onRowOrderChange}
      isCellEditable={props.isCellEditable}
      onCellEditStop={props.onCellEditStop}
      processRowUpdate={props.processRowUpdate}
      localeText={
        // locales[(props.currentLanguage as SupportedLanguages) ?? "it"]
        locales[currentLanguage() as SupportedLanguages]
      }
      // localeText={{
      //   toolbarExport: "",
      //   toolbarFilters: "",
      //   toolbarDensity: "",
      //   toolbarColumns: "",
      // }}
    />
  );
};

export default DmsDataGridPro;
